<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="auth-modal-overlay"
      role="presentation"
      @click.self="handleClose"
    >
      <div
        class="auth-modal-dock"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        @click.stop
      >
        <div class="auth-modal">
          <button
            type="button"
            class="auth-modal__close"
            :aria-label="t('auth.close')"
            @click="handleClose"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <div class="auth-modal__panel">
              <div class="auth-modal__panel-head">
                <img class="auth-modal__logo" :src="a.logo2025" alt="" />
              </div>

              <div class="auth-modal__intro">
                <div
                  id="auth-modal-title"
                  class="auth-modal__title"
                  role="heading"
                  aria-level="2"
                >
                  {{ t('auth.title') }}
                </div>
                <p class="auth-modal__desc">{{ t('auth.desc') }}</p>
              </div>

              <div class="auth-modal__fields">
              <div class="auth-modal__field-block">
                <div class="auth-modal__shell">
                  <AreaCodeSelect v-model="areaCode" @change="onAreaCodeChange" />
                  <span class="auth-modal__shell-divider" aria-hidden="true" />
                  <input
                    v-model.trim="phone"
                    type="tel"
                    class="auth-modal__shell-input"
                    :maxlength="phoneMaxLength"
                    :placeholder="t('auth.phonePlaceholder')"
                    autocomplete="tel-national"
                  />
                </div>
              </div>
              <div class="auth-modal__field-block">
                <div class="auth-modal__shell">
                  <input
                    v-model.trim="smsCode"
                    type="text"
                    class="auth-modal__shell-input auth-modal__shell-input--grow"
                    maxlength="8"
                    :placeholder="t('auth.codePlaceholder')"
                    autocomplete="one-time-code"
                  />
                  <span class="auth-modal__shell-divider" aria-hidden="true" />
                  <button
                    type="button"
                    class="auth-modal__shell-action"
                    :disabled="sendDisabled"
                    :aria-label="countdown > 0 ? t('auth.resendCountdown').replace(/\{n\}/g, String(countdown)) : t('auth.getCode')"
                    @click="onSendCode"
                  >
                    {{ countdown > 0 ? `${countdown}s` : t('auth.getCode') }}
                  </button>
                </div>
              </div>
              </div>

              <label class="auth-modal__agree">
                <input v-model="agreed" type="checkbox" class="auth-modal__agree-input" />
                <span class="auth-modal__agree-text">
                  {{ t('auth.agreePrefix') }}
                  <a
                    href="#"
                    class="auth-modal__link"
                    @click.prevent.stop="openLegalDoc('USER', $event)"
                  >{{ t('auth.userAgreement') }}</a>
                  {{ t('auth.agreeAnd') }}
                  <a
                    href="#"
                    class="auth-modal__link"
                    @click.prevent.stop="openLegalDoc('YSZC', $event)"
                  >{{ t('auth.privacy') }}</a>
                </span>
              </label>

              <button
                type="button"
                class="auth-modal__submit"
                :disabled="submitting"
                @click="onSubmit"
              >
                {{ submitting ? t('auth.submitting') : t('auth.login') }}
              </button>

              <div class="auth-modal__sub-row">
                <button type="button" class="auth-modal__text-link" @click="onRegisterHint">
                  {{ t('auth.autoRegister') }}
                </button>
              </div>
            </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed, onUnmounted } from 'vue'
import { showToast } from 'vant'
import { aiSkillsLandingAssets as a } from '@/views/landing/AISkillsLanding'
import { useAuthModal } from '@/composables/useAuthModal'
import { useLandingLocale } from '@/composables/useLandingLocale'
import { toLegacyLangType } from '@repo/common-utils/utils/appLocale'
import { tencentCaptcha } from '@repo/common-components/business-components/sendSms/utils'
import { sendLoginSmsCode, loginWeb } from '@/services/authApi'
import { notifySessionAuthChanged } from '@/composables/useSessionAuth'
import AreaCodeSelect from '@/components/auth/AreaCodeSelect.vue'
import { isProdDeployEnv } from '@/utils/deployEnv'

const { t, locale } = useLandingLocale()
const { visible, close } = useAuthModal()

/** 与复星财富 H5 登录页 builderPage 协议 key 一致：USER=用户协议，YSZC=隐私声明 */
type LegalDocKey = 'USER' | 'YSZC'

const H5_ORIGIN: Record<'sit' | 'uat' | 'prod', string> = {
  sit: 'https://sit.mfosunhani.com',
  uat: 'https://uat.mfosunhani.com',
  prod: 'https://h5.mfosunhani.com',
}

function resolveH5Origin(): string {
  const env = import.meta.env.VUE_APP_ENV as 'sit' | 'uat' | 'prod' | undefined
  if (env && H5_ORIGIN[env]) return H5_ORIGIN[env]
  return isProdDeployEnv() ? H5_ORIGIN.prod : H5_ORIGIN.sit
}

function buildLegalDocUrl(key: LegalDocKey): string {
  const langType = toLegacyLangType(locale.value)
  return `${resolveH5Origin()}/pages/builderPage.html#/?key=${key}&langType=${langType}`
}

function openLegalDoc(key: LegalDocKey, event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
  window.open(buildLegalDocUrl(key), '_blank', 'noopener,noreferrer')
}

const DEFAULT_AREA_CODE = '+852'

const areaCode = ref(DEFAULT_AREA_CODE)
const phone = ref('')
const smsCode = ref('')
const salt = ref('')
const agreed = ref(false)
const countdown = ref(0)
const submitting = ref(false)
/** 滑块验证或发码请求进行中，避免重复点击 */
const codeSending = ref(false)

/** 各区号手机号格式，与 H5 login 校验规则对齐 */
const PHONE_RULES: Record<string, RegExp> = {
  '+86': /^1\d{10}$/,
  '+852': /^\d{8}$/,
  '+853': /^\d{8}$/,
  '+886': /^0?\d{9}$/,
}

let prevAreaCode = DEFAULT_AREA_CODE

const phoneMaxLength = computed(() => {
  const ac = areaCode.value
  if (ac === '+86') return 11
  if (ac === '+852' || ac === '+853') return 8
  if (ac === '+886') return 10
  return 15
})

let countdownTimer: ReturnType<typeof setInterval> | null = null

const AUTH_TOAST_DURATION = 2000
const AUTH_TOAST_ICON =
  "data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='8' cy='8' r='6.5' stroke='white' stroke-width='1.2'/%3E%3Cpath d='M4.9 8.05L7.05 10.05L11.2 5.8' stroke='white' stroke-width='1.35' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"

/** 倒计时或发码进行中禁用；无效手机号由 onSendCode 内 toast 提示 */
const sendDisabled = computed(() => countdown.value > 0 || codeSending.value)

function isPhoneValid(p: string, ac: string): boolean {
  const rule = PHONE_RULES[ac] ?? /^\d+$/
  return rule.test(p)
}

function onAreaCodeChange(nextAreaCode: string) {
  if (nextAreaCode !== prevAreaCode) {
    phone.value = ''
    smsCode.value = ''
    salt.value = ''
    clearCountdown()
    prevAreaCode = nextAreaCode
  }
}

function clearCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
  countdown.value = 0
}

function startCountdown() {
  clearCountdown()
  countdown.value = 60
  countdownTimer = setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0) clearCountdown()
  }, 1000)
}

function resetForm() {
  areaCode.value = DEFAULT_AREA_CODE
  prevAreaCode = DEFAULT_AREA_CODE
  phone.value = ''
  smsCode.value = ''
  salt.value = ''
  agreed.value = false
  clearCountdown()
}

function handleClose() {
  close()
}

function showAuthToast(message: string) {
  showToast({
    message,
    icon: AUTH_TOAST_ICON,
    iconSize: '16px',
    duration: AUTH_TOAST_DURATION,
    className: 'auth-modal-toast auth-modal-toast--icon',
    wordBreak: 'normal',
  })
}

function showAuthTextToast(message: string) {
  showToast({
    message,
    type: 'text',
    duration: AUTH_TOAST_DURATION,
    className: 'auth-modal-toast',
    wordBreak: 'normal',
  })
}

function onRegisterHint() {
  showAuthToast(t('auth.registerHint'))
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && visible.value) handleClose()
}

watch(visible, (v) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = v ? 'hidden' : ''
  if (!v) {
    resetForm()
  }
  if (v) {
    window.addEventListener('keydown', onKeydown)
  } else {
    window.removeEventListener('keydown', onKeydown)
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
  clearCountdown()
})

/** 腾讯滑块 ret=2 表示用户主动关闭，无需 toast */
function isCaptchaCancelled(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { ret?: number }).ret === 2
}

async function onSendCode() {
  const ac = areaCode.value
  if (!isPhoneValid(phone.value, ac)) {
    showAuthTextToast(t('auth.invalidPhone'))
    return
  }
  if (codeSending.value) return
  codeSending.value = true
  try {
    let captcha: string
    try {
      captcha = await tencentCaptcha()
    } catch (err) {
      if (!isCaptchaCancelled(err)) {
        showAuthTextToast(t('auth.captchaFailed'))
      }
      return
    }
    const res = await sendLoginSmsCode(ac, phone.value, captcha)
    if (res.ok) {
      salt.value = res.salt
      startCountdown()
      showAuthToast(t('auth.codeSent'))
    } else if (res.reason === 'repeat') {
      showAuthTextToast(t('auth.sendTooOften'))
    } else {
      showAuthTextToast(t('auth.codeSendFailed'))
    }
  } catch {
    showAuthTextToast(t('auth.networkError'))
  } finally {
    codeSending.value = false
  }
}

async function onSubmit() {
  if (!agreed.value) {
    showAuthTextToast(t('auth.agreeRequired'))
    return
  }
  if (!isPhoneValid(phone.value, areaCode.value)) {
    showAuthTextToast(t('auth.phoneRequired'))
    return
  }

  if (!salt.value) {
    showAuthTextToast(t('auth.getCodeFirst'))
    return
  }
  if (!smsCode.value) {
    showAuthTextToast(t('auth.codeRequired'))
    return
  }

  submitting.value = true
  try {
    const res = await loginWeb(areaCode.value, phone.value, smsCode.value, salt.value)
    if (res.ok) {
      notifySessionAuthChanged()
      /* 真 UC 会话与仅引流态均视为登录成功，统一成功提示 */
      showAuthToast(t('auth.submitSuccess'))
      handleClose()
    } else if (res.reason === 'network') {
      showAuthTextToast(t('auth.networkError'))
    } else {
      showAuthTextToast(res.message || t('auth.invalidCode'))
    }
  } catch {
    showAuthTextToast(t('auth.networkError'))
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
:global(.auth-modal-toast) {
  box-sizing: border-box;
  width: fit-content;
  min-width: 0;
  min-height: 0;
  padding: 10px 18px 10px 16px;
  color: #fff;
  font-size: 14px;
  line-height: normal;
  white-space: nowrap;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 8px;
}

:global(.auth-modal-toast--icon) {
  flex-direction: row;
  gap: 6px;
}

:global(.auth-modal-toast--icon .van-toast__icon) {
  margin: 0;
}

:global(.auth-modal-toast--icon .van-toast__text) {
  margin: 0;
}

.auth-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  overflow: auto;
}

.auth-modal-dock {
  position: relative;
  flex-shrink: 0;
  width: 373px;
  max-width: 100%;
  border-radius: 20px;
  overflow: visible;
}

.auth-modal {
  --accent: #ff6907;
  --ink: #181818;
  --muted: #666;
  --muted2: #9c9c9c;
  --line: rgba(0, 0, 0, 0.06);
  --shell: #f7f7f7;
  --auth-panel: #ffffff;

  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 0;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font-size: 14px;
  line-height: normal;
  color: var(--ink);
  border: 1px solid #fff;
  border-radius: 20px;
  background:
    radial-gradient(circle at 36% -4%, #ffdbc3 0, rgba(255, 219, 195, 0.58) 10%, rgba(255, 255, 255, 0) 28%),
    var(--auth-panel);
}

/* ─── 表单区（整卡） ─── */
.auth-modal__panel {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
  width: 100%;
  padding: 24px 0;
  min-width: 0;
  flex-shrink: 0;
  background: transparent;
}

.auth-modal__panel-head {
  flex-shrink: 0;
  padding: 0 32px;
}

.auth-modal__logo {
  display: block;
  width: 80px;
  height: 27px;
  object-fit: contain;
}

.auth-modal__intro {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0 32px;
}

.auth-modal__close {
  position: absolute;
  top: 9px;
  right: 11px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  color: #666;
  background: transparent;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: color 150ms ease, background 150ms ease;
}

.auth-modal__close:hover {
  color: var(--ink);
  background: var(--line);
}

.auth-modal__title {
  margin: 0;
  padding: 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--ink);
  line-height: 34px;
}

.auth-modal__desc {
  margin: 0;
  color: var(--ink);
  font-size: 14px;
  line-height: normal;
}

.auth-modal__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px;
}

.auth-modal__field-block {
  min-width: 0;
}

.auth-modal__shell {
  display: flex;
  align-items: center;
  min-height: 52px;
  background: var(--shell);
  border-radius: 10px;
  overflow: hidden;
}

.auth-modal__shell-divider {
  flex-shrink: 0;
  width: 1px;
  height: 16px;
  margin: 0;
  background: rgba(0, 0, 0, 0.06);
}

.auth-modal__shell-input {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  height: 52px;
  padding: 0 16px;
  font-size: 14px;
  color: var(--ink);
  background: transparent;
  border: none;
  outline: none;
}

.auth-modal__shell-input--grow {
  flex: 1 1 auto;
}

.auth-modal__shell-input::placeholder {
  color: var(--muted2);
  opacity: 1;
}

.auth-modal__shell-action {
  flex-shrink: 0;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: var(--accent);
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: nowrap;
}

.auth-modal__shell-action:disabled {
  color: var(--muted2);
  opacity: 1;
  cursor: not-allowed;
}

.auth-modal__agree {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  box-sizing: border-box;
  width: 100%;
  padding: 0 24px;
  font-size: 12px;
  color: var(--muted);
  line-height: 1.5;
  cursor: pointer;
}

.auth-modal__agree-input {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin: 2px 6px 0 0;
  cursor: pointer;
  accent-color: var(--accent);
  vertical-align: top;
  appearance: none;
  border: 0.84px solid var(--muted);
  border-radius: 2.4px;
  background: transparent;
}

.auth-modal__agree-input:checked {
  border-color: var(--ink);
  background:
    url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3.1 6.05L5.05 7.95L8.9 3.9' stroke='white' stroke-width='1.35' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") center / 12px 12px no-repeat,
    var(--ink);
}

.auth-modal__agree-text {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  line-height: 1.5;
}

.auth-modal__link {
  color: var(--ink);
  text-decoration: none;
}

.auth-modal__link:hover {
  text-decoration: underline;
}

.auth-modal__submit {
  width: min(314px, calc(100% - 40px));
  align-self: center;
  padding: 12px 35px;
  font-size: 16px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(0.4deg, #341806 1.9%, #181818 137.9%);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: none;
  transition:
    background 0.15s ease,
    box-shadow 0.15s ease;
}

.auth-modal__submit:hover:not(:disabled) {
  background: linear-gradient(0.4deg, #ff893c 1.9%, #ff6907 137.9%);
  box-shadow: 0 14px 15.1px rgba(255, 131, 49, 0.11);
}

.auth-modal__submit:active:not(:disabled) {
  background: linear-gradient(0.4deg, #cf5302 1.9%, #ca5d15 137.9%);
  box-shadow: none;
}

.auth-modal__submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.auth-modal__sub-row {
  display: flex;
  justify-content: center;
  width: 100%;
}

.auth-modal__text-link {
  max-width: 100%;
  padding: 0;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: var(--muted2);
  text-align: center;
  background: none;
  border: none;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;
}

.auth-modal__text-link:hover {
  text-decoration: underline;
}

@media (max-width: 720px) {
  .auth-modal-dock {
    width: min(340px, calc(100vw - 32px));
  }

  .auth-modal {
    max-height: 90vh;
    font-size: 12px;
  }

  .auth-modal__panel {
    gap: 18px;
  }

  .auth-modal__fields {
    gap: 10px;
  }

  .auth-modal__shell {
    min-height: 40px;
    border-radius: 10px;
  }

  .auth-modal__shell-divider {
    margin: 8px 0;
  }

  .auth-modal__shell-input {
    padding: 0 10px;
    font-size: 13px;
  }

  .auth-modal__shell-action {
    padding: 0 10px;
    font-size: 12px;
  }

  .auth-modal__agree-input {
    width: 14px;
    height: 14px;
    margin-top: 1px;
  }

  .auth-modal__submit {
    padding: 10px 16px;
    font-size: 14px;
  }

  .auth-modal__text-link {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .auth-modal__panel {
    gap: 16px;
  }

  .auth-modal__shell {
    min-height: 38px;
    border-radius: 9px;
  }

  .auth-modal__shell-input {
    padding: 0 10px;
    font-size: 12px;
  }

  .auth-modal__shell-action {
    font-size: 11px;
    padding: 0 8px;
  }

  .auth-modal__submit {
    padding: 9px 14px;
    font-size: 13px;
  }

  .auth-modal__text-link {
    font-size: 11px;
  }
}
</style>
